import React from "react";

/**
 * FilterPills — compact, horizontally-scrollable pill row used to filter
 * dashboard lists by status + date. Designed to be cheap to drop into any
 * list page without altering layout or data fetching.
 *
 * Usage:
 *   const [status, setStatus] = useState("all");
 *   const [range, setRange]   = useState("all");
 *   <FilterPills
 *     testid="customer-bookings-filters"
 *     status={status} onStatusChange={setStatus}
 *     range={range}   onRangeChange={setRange}
 *     statusOptions={["all", "pending", "confirmed", "completed"]}
 *   />
 *
 *   const filtered = useMemo(
 *     () => applyBookingFilters(bookings, { status, range }),
 *     [bookings, status, range]
 *   );
 *
 * `statusOptions` is configurable so a Concierge dashboard can say
 * "Incoming / Active / Completed" while a Customer says
 * "Pending / Confirmed / Completed".
 */
const STATUS_LABEL = {
  all: "All",
  pending: "Pending",
  pending_acceptance: "Pending",
  incoming: "Incoming",
  confirmed: "Confirmed",
  accepted: "Accepted",
  active: "Active",
  paid: "Paid",
  completed: "Completed",
  canceled: "Canceled",
};
const RANGE_LABEL = {
  week: "This Week",
  month: "This Month",
  all: "All Time",
};

export default function FilterPills({
  status = "all",
  range = "all",
  onStatusChange,
  onRangeChange,
  statusOptions = ["all", "pending", "confirmed", "completed"],
  rangeOptions = ["week", "month", "all"],
  statusCounts,   // optional: { all: 12, pending: 4, confirmed: 3, completed: 5 }
  rangeCounts,    // optional: { week: 2, month: 6, all: 12 }
  testid,
  className = "",
}) {
  return (
    <div
      data-testid={testid}
      className={`flex gap-2 overflow-x-auto -mx-1 px-1 py-1 scrollbar-thin ${className}`}
    >
      {statusOptions.map((s) => (
        <Pill
          key={`s-${s}`}
          active={status === s}
          onClick={() => onStatusChange?.(s)}
          testid={`${testid}-status-${s}`}
          count={statusCounts?.[s]}
        >
          {STATUS_LABEL[s] || s}
        </Pill>
      ))}
      {/* Subtle divider so date pills read as a distinct group */}
      <div className="self-center w-px h-5 bg-[#071A2D]/10 mx-1 shrink-0"/>
      {rangeOptions.map((r) => (
        <Pill
          key={`r-${r}`}
          active={range === r}
          onClick={() => onRangeChange?.(r)}
          variant="date"
          testid={`${testid}-range-${r}`}
          count={rangeCounts?.[r]}
        >
          {RANGE_LABEL[r] || r}
        </Pill>
      ))}
    </div>
  );
}

const Pill = ({ active, onClick, children, variant, testid, count }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.14em] border transition ${
      active
        ? variant === "date"
          ? "bg-[#C9A45C] text-[#071A2D] border-[#C9A45C]"
          : "bg-[#064E3B] text-[#FFF8EE] border-[#064E3B]"
        : "bg-[#FFF8EE] text-[#071A2D]/65 border-[#071A2D]/12 hover:border-[#064E3B]/40 hover:text-[#064E3B]"
    }`}
  >
    <span>{children}</span>
    {typeof count === "number" && (
      <span
        data-testid={`${testid}-count`}
        className={`tabular-nums text-[10px] font-bold ${
          active
            ? variant === "date" ? "text-[#071A2D]/70" : "text-[#FFF8EE]/85"
            : "text-[#071A2D]/45"
        }`}
      >
        · {count}
      </span>
    )}
  </button>
);

/**
 * Convenience helper — compute the count for every status/range pill in one
 * pass so consumers don't run `applyBookingFilters` four times.
 */
export function computeBookingFilterCounts(items, {
  statusOptions = ["all", "pending", "confirmed", "completed"],
  rangeOptions = ["week", "month", "all"],
} = {}) {
  const arr = Array.isArray(items) ? items : [];
  const statusCounts = {};
  const rangeCounts = {};
  for (const s of statusOptions) {
    statusCounts[s] = applyBookingFilters(arr, { status: s, range: "all" }).length;
  }
  for (const r of rangeOptions) {
    rangeCounts[r] = applyBookingFilters(arr, { status: "all", range: r }).length;
  }
  return { statusCounts, rangeCounts };
}

/**
 * applyBookingFilters — shared helper so every dashboard filters with the
 * exact same status-aliasing + week/month boundaries.
 *
 * status:
 *   "all"        → no status filter
 *   "pending"    → matches Pending / Pending Acceptance / Incoming
 *   "confirmed"  → matches Confirmed / Accepted / Active / Paid
 *   "completed"  → matches Completed
 * range:
 *   "week"  → booking.date inside current ISO week (Mon..Sun)
 *   "month" → booking.date inside current calendar month
 *   "all"   → no date filter
 */
export function applyBookingFilters(items, { status = "all", range = "all" } = {}) {
  const arr = Array.isArray(items) ? items : [];
  const STATUS_GROUPS = {
    all: null,
    pending: ["Pending", "Pending Acceptance", "Incoming"],
    confirmed: ["Confirmed", "Accepted", "Active", "Paid"],
    completed: ["Completed"],
    canceled: ["Canceled", "Cancelled"],
  };
  const wanted = STATUS_GROUPS[status] || null;
  const now = new Date();
  // ISO week — Monday start
  const day = now.getDay() || 7;
  const monday = new Date(now); monday.setDate(now.getDate() - (day - 1)); monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6); sunday.setHours(23, 59, 59, 999);
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return arr.filter((it) => {
    if (wanted && !wanted.includes(it.status)) return false;
    if (range === "all") return true;
    const refIso = it.date || it.created_at;
    if (!refIso) return range === "all";
    const d = new Date(refIso);
    if (Number.isNaN(d.getTime())) return false;
    if (range === "week") return d >= monday && d <= sunday;
    if (range === "month") return d >= firstOfMonth && d <= lastOfMonth;
    return true;
  });
}
